import { create } from 'zustand';
import { useLive } from '../live/store';
import { ApiError, robotCmd } from '../api/client';
import { toast } from '../components/toast';
import { t } from '../i18n';

/** A handPush command in flight per robot (shared by the FAB, its popover, tile buttons and the mode chip). */
export const useHandPushPending = create<{ pending: Record<string, 'release' | 'lock'>; set: (serial: string, kind: 'release' | 'lock' | null) => void }>((set) => ({
  pending: {},
  set: (serial, kind) =>
    set((s) => {
      const pending = { ...s.pending };
      if (kind) pending[serial] = kind;
      else delete pending[serial];
      return { pending };
    }),
}));

/** status.handPush.enabled (plain toggle: no countdown) + the command in flight, if any. */
export function useHandPush(serial: string) {
  const enabled = useLive((s) => Boolean(s.robots[serial]?.status?.handPush?.enabled));
  const pending = useHandPushPending((s) => s.pending[serial] ?? null);
  return { enabled, pending };
}

/** Robots whose wheels are released (online only), for the FAB badge / summary. */
export function useReleasedSerials() {
  return useLive((s) =>
    s.robotOrder
      .filter((k) => s.robots[k]?.online && s.robots[k]?.status?.handPush?.enabled)
      .join('|'),
  )
    .split('|')
    .filter(Boolean);
}

/** Plain-words reason for a failed handPush command; null = the robot did not respond. */
function reasonOf(code: string, message: string): string | null {
  const c = code.toUpperCase();
  if (c === 'TIMEOUT' || c === 'OFFLINE' || c === 'NOT_CONNECTED' || c === 'ROBOT_OFFLINE' || c === 'GATEWAY_TIMEOUT') return null;
  if (c === 'UNKNOWN_COMMAND' || c === 'NOT_SUPPORTED' || c === 'UNSUPPORTED') return t('hand.whyUnsupported');
  if (c === 'EMERGENCY_STOP' || c === 'ESTOP') return t('hand.whyEstop');
  if (c === 'CHARGING') return t('hand.whyCharging');
  return message || code || t('hand.whyUnknown');
}

/** Send handPush and, on failure, say what happened in plain words ("Couldn’t release wheels · NAME did not respond" + Retry). */
async function handPushCmd(serial: string, enabled: boolean): Promise<boolean> {
  const live = useLive.getState();
  const pend = useHandPushPending.getState();
  if (pend.pending[serial]) return false; // one at a time per robot
  const name = live.robots[serial]?.name ?? serial;
  live.addLog(serial, 'cmd', '→ handPush', enabled ? 'on' : 'off');
  pend.set(serial, enabled ? 'release' : 'lock');
  let why: string | null;
  try {
    const r = await robotCmd(serial, 'handPush', { enabled });
    if (r.ok) {
      live.addLog(serial, 'ok', '✓ handPush', '');
      // show the new state right away; the next robotStatus confirms it
      const st = useLive.getState().robots[serial]?.status;
      if (st) useLive.getState().setStatus(serial, true, { ...st, handPush: { ...(st.handPush ?? {}), enabled } });
      return true;
    }
    live.addLog(serial, 'err', '✗ handPush', `${r.error.code}: ${r.error.message}`);
    why = reasonOf(r.error.code, r.error.message);
  } catch (e) {
    const status = e instanceof ApiError ? e.status : 0;
    live.addLog(serial, 'err', '✗ handPush', (e as Error).message);
    why = status === 504 || status === 503 || status === 0 ? null : status === 404 ? t('hand.whyUnsupported') : (e as Error).message;
  } finally {
    useHandPushPending.getState().set(serial, null);
  }
  const msg =
    why === null
      ? t(enabled ? 'hand.failReleaseSilent' : 'hand.failLockSilent', { name })
      : t(enabled ? 'hand.failRelease' : 'hand.failLock', { name, why });
  toast(msg, 'err', { label: t('hand.retry'), onClick: () => void handPushCmd(serial, enabled) });
  return false;
}

export const enableHandPush = (serial: string) => handPushCmd(serial, true);
export const lockWheels = (serial: string) => handPushCmd(serial, false);
